import type { BatchRequestItem } from '@/types/content'

/** Content type slugs matching C# [ContentModel] slug attributes. */
export const ContentType = {
  // Phase 1: Globals, Labels & Shared Registries
  SHARED_MESSAGES: 'booking-shared-messages',
  PROCESSING_LABELS: 'booking-processing-labels',
  NAV: 'booking-nav',
  PAGE_META_TITLES: 'booking-page-meta-titles',
  EXTERNAL_LINK: 'booking-external-link',
  PROGRESS_STEP: 'booking-progress-step',
  DISCLAIMER: 'booking-disclaimer',
  FAQ_ITEM: 'booking-faq-item',
  MODAL: 'booking-modal',
  UPGRADE_MODAL: 'booking-upgrade-modal',
  GALLERY_IMAGE: 'session-gallery-image',
  // Sessions listing
  SESSIONS_LISTING_PAGE: 'sessions-listing-page',
  SESSIONS_LISTING_COMPARISON_LABEL: 'sessions-listing-comparison-label',
  BUNDLE_COMPARISON_ITEM: 'bundle-comparison-item',
  BUNDLE_FEATURE_BULLET: 'bundle-feature-bullet',
  VALUE_SESSION_BUNDLE_DETAILS: 'value-session-bundle-details',
  BUNDLE_UPGRADE_MODAL: 'booking-bundle-upgrade-modal',
  BASIC_CONFIRM_DRAWER: 'booking-basic-confirm-drawer',
  VALUE_SESSION_BUNDLES_LISTING: 'value-session-bundles-listing',
  BUDGET_SESSION_BUNDLES_LISTING: 'budget-session-bundles-listing',
  BUNDLE_FEATURE_CALLOUT: 'bundle-feature-callout',
  SESSION_DEAL_CARD: 'session-deal-card',
  SCENE_INSPIRATION_MODAL: 'scene-inspiration-modal',
  SCENE_CATALOG_ENTRY: 'scene-catalog-entry',
  SESSION_DETAIL_PAGE: 'session-detail-page',
  SESSION_DETAIL_SECTION: 'session-detail-section',
  ADD_ON_CONTENT: 'booking-add-on-content',
  ADD_ON_IMAGE: 'booking-add-on-image',
  FINAL_PRINTS_PRICING_MODAL: 'final-prints-pricing-modal',
  SCHEDULE_SESSION_PAGE: 'schedule-session-page',
  SCENE_SELECTION_LABELS: 'scene-selection-labels',
  YEARBOOK_INFO_MODAL: 'yearbook-info-modal',
  BASIC_UPGRADE_COMPARISON_MODAL: 'basic-upgrade-comparison-modal',
  RS_PLUS_LOCATION_SELECTION_MODAL: 'rs-plus-location-selection-modal',
  // Checkout summary
  CHECKOUT_BASIC_UPGRADE: 'checkout-basic-upgrade',
  CHECKOUT_CART_UPSELL: 'checkout-cart-upsell',
  CHECKOUT_CART_UPSELL_FEATURE: 'checkout-cart-upsell-feature',
  CHECKOUT_SUMMARY_PAGE: 'checkout-summary-page',
  // Checkout pre-pay
  CHECKOUT_PREPAY_PAGE: 'checkout-prepay-page',
  CHECKOUT_PREPAY_CARD: 'checkout-prepay-card',
  CHECKOUT_PREPAY_IMAGE: 'checkout-prepay-image',
  CHECKOUT_PREMIER_RETOUCHING: 'checkout-premier-retouching',
  // Checkout terms
  CHECKOUT_TERMS_PAGE: 'checkout-terms-page',
  CHECKOUT_MODEL_RELEASE_TERMS: 'checkout-model-release-terms',
  // Checkout pay
  CHECKOUT_PAY_PAGE: 'checkout-pay-page',
  ORDER_COMPLETE_PAGE: 'order-complete-page',
  // Confirmation
  CONFIRMATION_PAGE: 'confirmation-page',
  // Error pages
  NOT_FOUND_PAGE: 'not-found-page',
  // Landing page
  LANDING_PAGE: 'landing-page',
  LANDING_HERO: 'landing-hero',
  LANDING_HEADLINE: 'landing-headline',
  LANDING_BOOKING_STEPS: 'landing-booking-steps',
  LANDING_CAROUSEL: 'landing-carousel',
  LANDING_SCENE_PREVIEW: 'landing-scene-preview',
  LANDING_SESSION_GLANCE: 'landing-session-glance',
  LANDING_LOCATION_MODAL: 'landing-location-modal',
  LANDING_LOCATION_OPTION: 'landing-location-option',
  LANDING_SCHOOL_MODALS: 'landing-school-modals'
} as const

export type ContentTypeSlug = (typeof ContentType)[keyof typeof ContentType]

/** Shared messages — fetched once at app-init and reused by every consumer. */
export const sharedMessagesBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SHARED_MESSAGES, identifier: 'default' }
]

/** Processing-overlay labels — fetched once at app-init and reused by every `setAppProcessing` callsite. */
export const processingLabelsBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.PROCESSING_LABELS, identifier: 'default' }
]

/** Nav CTA labels — fetched once at app-init, rendered on every page. */
export const navBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.NAV, identifier: 'default' }
]

/** Page meta titles — fetched once at app-init, consumed by the router `beforeEach`. */
export const pageMetaTitlesBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.PAGE_META_TITLES, identifier: 'default' }
]

/** Progress step items shared across most booking pages. */
export const progressStepBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.PROGRESS_STEP, identifier: 'home' },
  { contentType: ContentType.PROGRESS_STEP, identifier: 'choose-session' },
  { contentType: ContentType.PROGRESS_STEP, identifier: 'select-scenes' },
  { contentType: ContentType.PROGRESS_STEP, identifier: 'schedule-session' },
  { contentType: ContentType.PROGRESS_STEP, identifier: 'review-checkout' }
]

/** Landing page parent entry — sections resolve via ContentRef. */
export const landingPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.LANDING_PAGE, identifier: 'default' }
]

/** Sessions listing page parent entry. */
export const sessionsListingPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SESSIONS_LISTING_PAGE, identifier: 'default' },
  { contentType: ContentType.FINAL_PRINTS_PRICING_MODAL, identifier: 'default' },
  { contentType: ContentType.VALUE_SESSION_BUNDLES_LISTING, identifier: 'default' },
  { contentType: ContentType.BUDGET_SESSION_BUNDLES_LISTING, identifier: 'default' }
]

/** Comparison feature labels for the bundle comparison table. */
export const comparisonLabelBatchItems: BatchRequestItem[] = [
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-yearbookPhotos'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-privatePhotographer'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-studioScenes'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-dryHairStyling'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-makeUpArtist'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-destinationScene'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-outdoorCityAndRusticLocations'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-outdoorScene'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-frontOfLinePass'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-premierRetouching'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-freeDigitals'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-creditTowardsCollections'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-portraitsForPreview'
  },
  {
    contentType: ContentType.SESSIONS_LISTING_COMPARISON_LABEL,
    identifier: 'comparison-packagesStartingAt'
  }
]

/** Bundle upgrade modals — one entry per upgrade target. */
export const bundleUpgradeModalBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.BUNDLE_UPGRADE_MODAL, identifier: 'upgrade-to-simple' },
  { contentType: ContentType.BUNDLE_UPGRADE_MODAL, identifier: 'upgrade-to-standard' },
  { contentType: ContentType.BUNDLE_UPGRADE_MODAL, identifier: 'upgrade-to-legacy' },
  { contentType: ContentType.BUNDLE_UPGRADE_MODAL, identifier: 'upgrade-to-most-popular' },
  { contentType: ContentType.BUNDLE_UPGRADE_MODAL, identifier: 'upgrade-to-elite' },
  { contentType: ContentType.BUNDLE_UPGRADE_MODAL, identifier: 'upgrade-to-elite-plus' },
  { contentType: ContentType.BUNDLE_UPGRADE_MODAL, identifier: 'upgrade-to-elite-unlimited' },
  {
    contentType: ContentType.BUNDLE_UPGRADE_MODAL,
    identifier: 'upgrade-elite-plus-to-elite-unlimited'
  }
]

/** Session deal cards — filtered at render time by promotionId field (5 entries). */
export const sessionDealBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SESSION_DEAL_CARD, identifier: 'deal-elitebonus-0' },
  { contentType: ContentType.SESSION_DEAL_CARD, identifier: 'deal-elitestyle-1' },
  { contentType: ContentType.SESSION_DEAL_CARD, identifier: 'deal-elite50-2' },
  { contentType: ContentType.SESSION_DEAL_CARD, identifier: 'deal-freescene-3' },
  { contentType: ContentType.SESSION_DEAL_CARD, identifier: 'deal-elitebonus-4' }
]

/** Scene inspiration modal — parent with ContentRef children (MaxDepth=3). */
export const sceneInspirationModalBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SCENE_INSPIRATION_MODAL, identifier: 'default' }
]

/** Schedule session page parent entry — sections resolve via ContentRef. */
export const scheduleSessionPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SCHEDULE_SESSION_PAGE, identifier: 'default' }
]

/** Scene selection page content. */
export const sceneSelectionBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SCENE_SELECTION_LABELS, identifier: 'default' }
]

/** Scene catalog entries — master scene display data. */
export const sceneCatalogBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'yearbook' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'center-stage' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'wonderlight' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'prime-time' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'shadowland' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'timeless' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'emerge' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'living-walls' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'exposure' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'outdoor' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'city' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'rustic' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'choose-your-destination' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'lake' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'beach' },
  { contentType: ContentType.SCENE_CATALOG_ENTRY, identifier: 'waterfall' }
]

/** Confirmation page parent entry — children resolve via ContentRef. */
export const confirmationPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.CONFIRMATION_PAGE, identifier: 'default' }
]

/** Checkout pay page parent entry. */
export const checkoutPayPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.CHECKOUT_PAY_PAGE, identifier: 'default' }
]

/** Order complete page — Stripe return-route messages. */
export const orderCompletePageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.ORDER_COMPLETE_PAGE, identifier: 'default' }
]

/** Not Found (404) page. */
export const notFoundPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.NOT_FOUND_PAGE, identifier: 'default' }
]

/** Checkout terms page parent entry + model release terms. */
export const checkoutTermsPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.CHECKOUT_TERMS_PAGE, identifier: 'default' },
  { contentType: ContentType.CHECKOUT_MODEL_RELEASE_TERMS, identifier: 'default' }
]

/** Checkout pre-pay page parent entry — cards resolve via ContentRef. */
export const checkoutPrepayPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.CHECKOUT_PREPAY_PAGE, identifier: 'default' },
  { contentType: ContentType.CHECKOUT_PREMIER_RETOUCHING, identifier: 'default' }
]

/** Checkout summary page parent entry — children resolve via ContentRef. */
export const checkoutSummaryPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.CHECKOUT_SUMMARY_PAGE, identifier: 'default' }
]

/** Gallery image items for all 12 scene identifiers. */
export const galleryImageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'classic-studio' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'modern-white' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'dramatic-black' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'urban-loft' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'garden-terrace' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'downtown-cityscape' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'beach-sunset' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'mountain-vista' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'autumn-woods' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'vintage-parlor' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'sports-arena' },
  { contentType: ContentType.GALLERY_IMAGE, identifier: 'graduation-stage' }
]

/** Session detail page parent entries (4 pages — sections resolve via ContentRef). */
export const sessionDetailPageBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.SESSION_DETAIL_PAGE, identifier: 'elite' },
  { contentType: ContentType.SESSION_DETAIL_PAGE, identifier: 'elite-plus' },
  { contentType: ContentType.SESSION_DETAIL_PAGE, identifier: 'elite-unlimited' },
  { contentType: ContentType.SESSION_DETAIL_PAGE, identifier: 'most-popular' }
]

/** Add-on content entries keyed by API bundle identifier. */
export const addOnContentBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.ADD_ON_CONTENT, identifier: 'HAIR_OR_MAKEUP' },
  { contentType: ContentType.ADD_ON_CONTENT, identifier: 'HAIR' },
  { contentType: ContentType.ADD_ON_CONTENT, identifier: 'MAKEUP' },
  { contentType: ContentType.ADD_ON_CONTENT, identifier: 'ADDON_OUTDOOR' },
  { contentType: ContentType.ADD_ON_CONTENT, identifier: 'ADDITIONAL_POSE' },
  { contentType: ContentType.ADD_ON_CONTENT, identifier: 'BLACK_AND_WHITE' }
]

/** Prepay card entries keyed by API bundle identifier. */
export const prepayCardBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.CHECKOUT_PREPAY_CARD, identifier: 'PREPAY_ALLDIGITALS' },
  { contentType: ContentType.CHECKOUT_PREPAY_CARD, identifier: 'PREPAY_DIGITAL_PORTFOLIO' },
  { contentType: ContentType.CHECKOUT_PREPAY_CARD, identifier: 'PREPAY_BASIC_ALLDIGITALS' }
]

/** Cart upsell entries keyed by API add-on bundle identifier. */
export const cartUpsellBatchItems: BatchRequestItem[] = [
  { contentType: ContentType.CHECKOUT_CART_UPSELL, identifier: 'BOOKING_ARTISTTOUCH' },
  { contentType: ContentType.CHECKOUT_CART_UPSELL, identifier: 'BW_PROOFS' }
]
